import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BRUSCHETTA_ITEMS,
  DRINK_ITEMS,
  OFEN_GEBACKENE_PASTA,
  PIZZA_EXTRA_OPTIONS,
  PIZZA_EXTRA_GROUPS,
  SIGNATURE_PIZZAS,
} from '../constants';
import SectionHeading from './SectionHeading';

const euroFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

const parsePriceToCents = (price) =>
  Math.round(Number(price.replace('EUR', '').trim().replace(',', '.')) * 100);

const formatPriceFromCents = (cents) => euroFormatter.format(cents / 100);

const PizzaCard = ({ pizza, isExtrasOpen, onExtrasToggle, rootRef }) => {
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [extrasQuery, setExtrasQuery] = useState('');
  const [openExtraGroupId, setOpenExtraGroupId] = useState(null);

  useEffect(() => {
    if (!pizza.isVegetarian) return;

    setSelectedExtras((currentExtras) =>
      currentExtras.filter((extraId) => {
        const extra = PIZZA_EXTRA_OPTIONS.find(
          (option) => option.id === extraId
        );
        return extra?.groupId !== 'fleisch' && extra?.groupId !== 'fisch';
      })
    );

    setOpenExtraGroupId((currentGroupId) =>
      currentGroupId === 'fleisch' || currentGroupId === 'fisch'
        ? null
        : currentGroupId
    );
  }, [pizza.isVegetarian]);

  useEffect(() => {
    if (isExtrasOpen) return;
    setExtrasQuery('');
    setOpenExtraGroupId(null);
  }, [isExtrasOpen]);

  const toggleExtra = (extraId) => {
    setSelectedExtras((currentExtras) =>
      currentExtras.includes(extraId)
        ? currentExtras.filter((id) => id !== extraId)
        : [...currentExtras, extraId]
    );
  };

  const normalizedExtrasQuery = extrasQuery.trim().toLowerCase();
  const extraGroups = pizza.isVegetarian
    ? PIZZA_EXTRA_GROUPS.filter(
        (group) => group.id !== 'fleisch' && group.id !== 'fisch'
      )
    : PIZZA_EXTRA_GROUPS;

  const extrasTotalCents = selectedExtras.reduce((sum, extraId) => {
    const extra = PIZZA_EXTRA_OPTIONS.find((option) => option.id === extraId);
    return sum + (extra?.priceCents || 0);
  }, 0);
  const totalPriceCents = parsePriceToCents(pizza.price) + extrasTotalCents;

  return (
    <article
      ref={rootRef}
      className="group overflow-hidden rounded-[2rem] border border-stone-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative h-80 overflow-hidden">
        <img
          src={pizza.imageUrl}
          alt={pizza.imageAlt}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {pizza.badge ? (
          <div className="absolute left-5 top-5 rounded-full bg-white/95 px-2 py-1 text-xs font-semibold tracking-[0.18em] text-stone-900 shadow-sm ring-1 ring-stone-200">
            {pizza.badge}
          </div>
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-950/20 to-transparent" />
        <div className="absolute bottom-3 left-5 right-5">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-orange-200">
            {pizza.category}
          </p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-white">
            {pizza.name}
          </h3>
          {pizza.allergens ? (
            <p className="mt-1 text-sm font-medium text-stone-100">
              {pizza.allergens}
            </p>
          ) : null}
        </div>
      </div>

      <div className="p-4">
        <p className="text-base leading-7 text-stone-700">
          {pizza.description}
        </p>

        {isExtrasOpen ? (
          <div className="mt-4 rounded-[1.5rem] border border-stone-200 bg-stone-50 p-3">
            <div className="mb-3">
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-stone-600">
                Extras suchen
              </label>
              <input
                value={extrasQuery}
                onChange={(event) => setExtrasQuery(event.target.value)}
                placeholder="z.B. Mozzarella, Zwiebeln, Dip…"
                className="mt-2 w-full rounded-2xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 shadow-sm focus:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>

            <div className="grid gap-2">
              {extraGroups.map((group) => {
                const groupOptions = group.options.filter((option) =>
                  normalizedExtrasQuery
                    ? option.name.toLowerCase().includes(normalizedExtrasQuery)
                    : true
                );

                if (!groupOptions.length) return null;

                const isOpen =
                  normalizedExtrasQuery.length > 0 ||
                  openExtraGroupId === group.id;

                return (
                  <div
                    key={group.id}
                    className="overflow-hidden rounded-2xl border border-stone-200 bg-white"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenExtraGroupId((currentId) =>
                          currentId === group.id ? null : group.id
                        )
                      }
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-semibold text-stone-900"
                      aria-expanded={isOpen}
                      aria-controls={`extras-group-${group.id}`}
                    >
                      <span>{group.title}</span>
                      <span className="shrink-0 text-stone-700">
                        +{formatPriceFromCents(group.priceCents)}
                      </span>
                    </button>

                    {isOpen ? (
                      <div
                        id={`extras-group-${group.id}`}
                        className="border-t border-stone-200 bg-stone-50 p-2"
                      >
                        <div className="grid gap-1.5">
                          {groupOptions.map((option) => {
                            const isSelected = selectedExtras.includes(
                              option.id
                            );
                            const optionDetails = PIZZA_EXTRA_OPTIONS.find(
                              (extra) => extra.id === option.id
                            );

                            return (
                              <label
                                key={option.id}
                                className={`flex cursor-pointer items-center justify-between rounded-2xl border px-3 py-2 text-sm transition ${
                                  isSelected
                                    ? 'border-orange-300 bg-orange-50 text-stone-950'
                                    : 'border-stone-200 bg-white text-stone-700'
                                }`}
                              >
                                <span className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleExtra(option.id)}
                                    className="h-4 w-4 rounded border-stone-300 text-orange-700 focus:ring-orange-500"
                                  />
                                  <span>{option.name}</span>
                                </span>
                                <span className="font-semibold text-stone-900">
                                  +
                                  {formatPriceFromCents(
                                    optionDetails?.priceCents ??
                                      group.priceCents
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex items-end justify-between gap-4 border-t border-stone-200 pt-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-stone-600">
              Gesamtpreis
            </p>
            <p className="mt-1 text-2xl font-semibold text-stone-950">
              {formatPriceFromCents(totalPriceCents)}
            </p>
            <p className="mt-1 text-sm text-stone-600">
              Basis {pizza.price}
              {extrasTotalCents > 0
                ? ` + Extras ${formatPriceFromCents(extrasTotalCents)}`
                : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onExtrasToggle}
            className="rounded-full border border-stone-300 px-3 py-2 text-sm font-semibold text-stone-900 transition group-hover:border-stone-950"
          >
            {isExtrasOpen ? 'Auswahl schließen' : 'Extras wählen'}
          </button>
        </div>
      </div>
    </article>
  );
};

const MenuSectionDivider = ({ title }) => (
  <div className="mt-14 flex items-center gap-4">
    <div className="h-px flex-1 bg-stone-300" />
    <h3 className="shrink-0 text-lg font-semibold tracking-[0.18em] text-stone-900">
      {title}
    </h3>
    <div className="h-px flex-1 bg-stone-300" />
  </div>
);

const TextMenuItem = ({ item }) => (
  <article className="border-b border-stone-200 pb-4 last:border-b-0 last:pb-0">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-3">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        {item.number ? (
          <span className="shrink-0 text-xl font-semibold text-stone-950">
            {item.number}.
          </span>
        ) : null}
        <h4 className="text-xl font-semibold text-stone-950">{item.name}</h4>
        {item.allergens ? (
          <span className="text-sm text-stone-600">{item.allergens}</span>
        ) : null}
      </div>
      <div className="hidden flex-1 border-b border-dotted border-stone-400 sm:block" />
      <p className="shrink-0 text-xl font-semibold text-stone-950">
        {item.price.replace('EUR', '€')}
      </p>
    </div>
    {item.description ? (
      <p
        className={`mt-2 pl-0 text-lg leading-7 text-stone-700 ${
          item.number ? 'sm:pl-8' : ''
        }`}
      >
        {item.description}
      </p>
    ) : null}
  </article>
);

const Speisekarte = ({ onContactClick }) => {
  const [activeSection, setActiveSection] = useState('pizza-classic');
  const [openExtrasPizzaId, setOpenExtrasPizzaId] = useState(null);
  const pizzaClassicRef = useRef(null);
  const kidsPizzaRef = useRef(null);
  const pizzaBrotRef = useRef(null);
  const pastaRef = useRef(null);
  const bruschettaRef = useRef(null);
  const drinksRef = useRef(null);
  const pizzaCardNodesRef = useRef(new Map());

  useEffect(() => {
    if (!openExtrasPizzaId) return;

    const handlePointerDown = (event) => {
      const cardNode = pizzaCardNodesRef.current.get(openExtrasPizzaId);
      if (!cardNode) return;
      if (!cardNode.contains(event.target)) setOpenExtrasPizzaId(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpenExtrasPizzaId(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openExtrasPizzaId]);

  const classicPizzas = useMemo(
    () =>
      SIGNATURE_PIZZAS.filter((pizza) => pizza.category === 'Pizza Classic'),
    []
  );

  const kidsPizzas = useMemo(
    () => SIGNATURE_PIZZAS.filter((pizza) => pizza.category === 'Kinder Pizza'),
    []
  );

  const pizzaBrotItems = useMemo(
    () =>
      SIGNATURE_PIZZAS.filter(
        (pizza) => pizza.category === 'Pizzeta Pizza Stücke'
      ),
    []
  );

  const menuFilters = [
    {
      id: 'pizza-classic',
      label: 'Pizza Classic',
      ref: pizzaClassicRef,
      isVisible: classicPizzas.length > 0,
    },
    {
      id: 'pizza-brot',
      label: 'Pizzeta Pizza Stücke',
      ref: pizzaBrotRef,
      isVisible: pizzaBrotItems.length > 0,
    },
    {
      id: 'kinder-pizza',
      label: 'Kinder Pizza',
      ref: kidsPizzaRef,
      isVisible: kidsPizzas.length > 0,
    },
    {
      id: 'ofen-gebackene-pasta',
      label: 'Ofen gebackene Pasta',
      ref: pastaRef,
      isVisible: OFEN_GEBACKENE_PASTA.length > 0,
    },
    {
      id: 'bruschetta',
      label: 'Bruschetta',
      ref: bruschettaRef,
      isVisible: BRUSCHETTA_ITEMS.length > 0,
    },
    {
      id: 'getraenke',
      label: 'Getränke 0,33l',
      ref: drinksRef,
      isVisible: DRINK_ITEMS.length > 0,
    },
  ].filter((section) => section.isVisible);

  const handleSectionFilterClick = (section) => {
    setActiveSection(section.id);
    section.ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="section-surface section-surface-light">
      <div className="mx-auto max-w-7xl px-6 pb-20 pt-2 md:pb-20 md:pt-8 lg:px-10 lg:pb-24 lg:pt-10">
        <div className="max-w-4xl">
          <SectionHeading
            eyebrow="Speisekarte"
            title="Pizza Classic, frische Zutaten, frisch serviert."
            titleClassName="text-2xl md:text-5xl"
            description="Unsere Klassiker mit frischen Zutaten und echten Bildern auf einen Blick."
          />
        </div>

        {menuFilters.length ? (
          <div className="mt-10 flex flex-wrap gap-3">
            {menuFilters.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => handleSectionFilterClick(section)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  activeSection === section.id
                    ? 'bg-stone-950 text-white'
                    : 'bg-white text-stone-700 ring-1 ring-stone-200 hover:ring-stone-400'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        ) : null}

        {classicPizzas.length ? (
          <div ref={pizzaClassicRef} className="section-anchor mt-10">
            <MenuSectionDivider
              title={
                <>
                  Pizza Classic
                  <span className="ml-2 text-sm font-normal tracking-normal text-stone-700">
                    32cm Größe Ø
                  </span>
                </>
              }
            />
            <div className="mt-8 grid items-start gap-6 md:grid-cols-2 xl:grid-cols-3">
              {classicPizzas.map((pizza) => (
                <PizzaCard
                  key={pizza.id}
                  pizza={pizza}
                  isExtrasOpen={openExtrasPizzaId === pizza.id}
                  onExtrasToggle={() =>
                    setOpenExtrasPizzaId((currentId) =>
                      currentId === pizza.id ? null : pizza.id
                    )
                  }
                  rootRef={(node) => {
                    if (node) pizzaCardNodesRef.current.set(pizza.id, node);
                    else pizzaCardNodesRef.current.delete(pizza.id);
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}

        {pizzaBrotItems.length ? (
          <div ref={pizzaBrotRef} className="section-anchor">
            <MenuSectionDivider
              title={
                <>
                  Pizzeta Pizza Stücke
                  <span className="ml-2 text-sm font-normal tracking-normal text-stone-700">
                    28cm Größe
                  </span>
                </>
              }
            />
            <div className="mt-8 grid items-start gap-6 md:grid-cols-2 xl:grid-cols-3">
              {pizzaBrotItems.map((pizza) => (
                <PizzaCard
                  key={pizza.id}
                  pizza={pizza}
                  isExtrasOpen={openExtrasPizzaId === pizza.id}
                  onExtrasToggle={() =>
                    setOpenExtrasPizzaId((currentId) =>
                      currentId === pizza.id ? null : pizza.id
                    )
                  }
                  rootRef={(node) => {
                    if (node) pizzaCardNodesRef.current.set(pizza.id, node);
                    else pizzaCardNodesRef.current.delete(pizza.id);
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}

        {kidsPizzas.length ? (
          <div ref={kidsPizzaRef} className="section-anchor">
            <MenuSectionDivider
              title={
                <>
                  Kinder Pizza
                  <span className="ml-2 text-sm font-normal tracking-normal text-stone-700">
                    28cm Größe Ø
                  </span>
                </>
              }
            />
            <div className="mt-8 grid items-start gap-6 md:grid-cols-2 xl:grid-cols-3">
              {kidsPizzas.map((pizza) => (
                <PizzaCard
                  key={pizza.id}
                  pizza={pizza}
                  isExtrasOpen={openExtrasPizzaId === pizza.id}
                  onExtrasToggle={() =>
                    setOpenExtrasPizzaId((currentId) =>
                      currentId === pizza.id ? null : pizza.id
                    )
                  }
                  rootRef={(node) => {
                    if (node) pizzaCardNodesRef.current.set(pizza.id, node);
                    else pizzaCardNodesRef.current.delete(pizza.id);
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}

        {OFEN_GEBACKENE_PASTA.length ? (
          <div ref={pastaRef} className="section-anchor">
            <MenuSectionDivider title="Ofen gebackene Pasta" />
            <div className="mt-8 rounded-[2rem] border border-stone-200 bg-white px-6 py-6 shadow-sm">
              <p className="text-lg leading-7 text-stone-700">
                Alle Pastagerichte werden mit Mozzarella zubereitet.
              </p>
              <div className="mt-6 space-y-5">
                {OFEN_GEBACKENE_PASTA.map((item) => (
                  <TextMenuItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {BRUSCHETTA_ITEMS.length ? (
          <div ref={bruschettaRef} className="section-anchor">
            <MenuSectionDivider title="Bruschetta" />
            <div className="mt-8 rounded-[2rem] border border-stone-200 bg-white px-6 py-6 shadow-sm">
              <div className="space-y-5">
                {BRUSCHETTA_ITEMS.map((item) => (
                  <TextMenuItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-6 rounded-[1.75rem] border border-stone-200 bg-stone-50 px-6 py-4 text-sm text-stone-700 shadow-sm">
          <p className="leading-6">
            <span className="block">
              <span className="font-semibold text-stone-950">
                Zusatzstoffe:
              </span>{' '}
              1) mit Geschmacksverstärker, 2) geschwärzt, 3) mit Farbstoff, 4)
              mit Konservierungsstoffen, 5) mit Antioxidationsmittel, 6)
              koffeinhaltig, 7) mit Zuckerarten und Süßungsmitteln, 8) mit
              Phosphat, 9) mit Emulgatoren, 10) enthält eine Phenylalaninquelle,
              12) mit Stabilisatoren.
            </span>
            <span className="mt-2 block">
              <span className="font-semibold text-stone-950">Allergene:</span>{' '}
              (a) Gluten, (b) Krebstiere, (c) Eier, (d) Sellerie, (e) Erdnüsse,
              (f) Fisch, (m) Milch,
            </span>
            <span className="block">
              (n) Senf, (s) Soja, (v) Schwefeloxide/Sulfite, (w) Schwefeldioxid.
            </span>
          </p>
        </div>

        {DRINK_ITEMS.length ? (
          <div ref={drinksRef} className="section-anchor">
            <MenuSectionDivider title="Getränke 0,33l" />
            <div className="mt-8 rounded-[2rem] border border-stone-200 bg-white px-6 py-6 shadow-sm">
              <div className="space-y-5">
                {DRINK_ITEMS.map((item) => (
                  <TextMenuItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-10 grid gap-6 rounded-[2rem] bg-stone-950 px-7 py-8 text-stone-50 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <p className="text-sm uppercase tracking-[0.28em] text-stone-300">
              Für einen professionelleren Eindruck
            </p>
            <p className="mt-3 max-w-3xl text-lg leading-8 text-stone-200">
              Gerne können Sie Ihre Bestellung vorab bei uns aufgeben und sie
              zur vereinbarten Uhrzeit frisch zubereitet abholen. So sparen Sie
              Wartezeit und Ihre Pizza ist genau dann bereit, wenn Sie
              vorbeikommen.
            </p>
          </div>
          <button
            type="button"
            onClick={onContactClick}
            className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-stone-950 transition hover:bg-orange-100"
          >
            Vorbestellung anfragen
          </button>
        </div>
      </div>
    </div>
  );
};

export default Speisekarte;
